import { FC, useEffect } from "react";
import { useForm, SubmitHandler, Controller } from "react-hook-form";

import { Button, Input, Label, Modal } from "../../common";
import { FormErrorMessage } from "../../messages";
import { useDeclineInvoiceMutation } from "../../../store/services/invoices";
import { DeclineInvoicePayload } from "../../../types/InvoiceTypes";

type Props = {
  id: string;
  isOpen: boolean;
  onClose: () => void;
};

export const DeclineInvoiceModal: FC<Props> = ({ id, isOpen, onClose }) => {
  const {
    handleSubmit,
    control,
    formState: { isValid },
  } = useForm<Omit<DeclineInvoicePayload, "id">>({
    defaultValues: {
      declined_reason: "",
    },
  });

  const [declineInvoice, { isSuccess, isLoading, isError, error }] =
    useDeclineInvoiceMutation();

  const onSubmit: SubmitHandler<Omit<DeclineInvoicePayload, "id">> = async ({
    declined_reason,
  }) => {
    declineInvoice({ id, declined_reason });
  };

  useEffect(() => {
    if (isSuccess) {
      onClose();
    }
  }, [isSuccess, onClose]);

  return (
    <Modal
      title="You are about to decline the invoice"
      subtitle="Please describe the reason to decline the invoice"
      isOpen={isOpen}
      close={onClose}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-16">
        <Label label="Reason">
          <Controller
            name="declined_reason"
            control={control}
            rules={{ required: true }}
            render={({ field }) => <Input {...field} type="text" />}
          />
        </Label>

        <FormErrorMessage isError={isError} error={error} />

        <div className="flex flex-col items-center justify-between gap-8">
          <Button
            type="submit"
            variant={Button.variant.CRITICAL}
            className="w-full"
            disabled={!isValid || isLoading}
          >
            Decline invoice
          </Button>

          <div className="text-sm text-center text-gray-300">
            The payer will see the reason and be able to adjust the invoice
          </div>

          <Button
            variant={Button.variant.TERTIARY}
            className="w-full"
            onClick={() => onClose()}
            disabled={isLoading}
          >
            Cancel
          </Button>
        </div>
      </form>
    </Modal>
  );
};
